import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaSMSDataV4Service, serviceName } from '../service';
import { GetThreadDetailsIO, ServiceQueries } from '../types';
import { useSMSDataV4Queries } from './use-sms-data-v4-queries';
import { UseSMSDataV4QueryEndpointArgs, useSMSDataV4Query } from './use-sms-data-v4-query';

/**
 * A hook that returns a query for the `GetThreadDetails` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetThreadDetailsQuery = <E = unknown, D = GetThreadDetailsIO['output']>(
  args: UseSMSDataV4QueryEndpointArgs<'GetThreadDetails', E, D>
) =>
  useSMSDataV4Query({
    endpointName: 'GetThreadDetails',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GetThreadDetails` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetThreadDetailsQueries = <E = unknown, D = GetThreadDetailsIO['output']>(
  argsArr: UseSMSDataV4QueryEndpointArgs<'GetThreadDetails', E, D>[]
) =>
  useSMSDataV4Queries(
    argsArr.map((args) => ({
      endpointName: 'GetThreadDetails',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `GetThreadDetails` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchGetThreadDetails = () => {
  const queryClient = useQueryClient();

  const fetchGetThreadDetails = async <E = unknown, D = GetThreadDetailsIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseSMSDataV4QueryEndpointArgs<'GetThreadDetails', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      GetThreadDetailsIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetThreadDetails'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        GetThreadDetailsIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetThreadDetails'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'GetThreadDetails'>({
          serviceName,
          endpointName: 'GetThreadDetails',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaSMSDataV4Service.GetThreadDetails(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchGetThreadDetails;
};
